/**
 * The little bit of markdown an agent actually writes in chat, parsed into blocks.
 *
 * Every chat pane rendered `{message.content}` as raw text inside `whitespace-pre-wrap`,
 * so a reply reading "your topic is **Age & Life Stage**" arrived with its asterisks
 * showing and a bulleted list arrived as a wall of hyphens. The models emit markdown
 * because that is what models emit; the panes were the only thing not treating it as
 * such.
 *
 * NO DEPENDENCY. react-markdown is ESM-only and CRA's jest cannot resolve it — the same
 * wall react-router-dom v7 and react-dnd sit behind — so pulling it in to bold a phrase
 * would cost every test that touches a chat pane. What agents write here is a handful of
 * constructs, and they are cheap to handle.
 *
 * PARSING ONLY, no React: the renderer (`ChatMarkdown.tsx`) is a separate file so this
 * one can be tested — and named differently rather than `chatMarkdown.ts`, because a
 * case-insensitive filesystem cannot tell that apart from `ChatMarkdown.tsx` and the
 * two modules collide.
 *
 * Deliberately NOT supported: raw HTML (never interpreted — spans become text nodes, so
 * there is nothing to inject), links, tables, block quotes and fenced code. Add them when
 * something actually emits them, with a test that says what it emits.
 */

/** A run of text with the emphasis that applies to it. */
export interface Span {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
}

export type Block =
  | { kind: 'p'; spans: Span[] }
  | { kind: 'h'; spans: Span[] }
  | { kind: 'ul'; items: Span[][] }
  | { kind: 'ol'; items: Span[][] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;

/**
 * `**bold**`, `` `code` ``, `*italic*` — in that order, longest marker first, or the
 * opening `**` of a bold run would match as an empty italic.
 *
 * UNDERSCORES ARE NOT ITALICS here, on purpose. This app's prose is full of
 * `snake_case` — field names, statuses like AGENT_GENERATED, tool names — and treating
 * `_` as emphasis turns "set suggested_prompts and research_sources" into one italic
 * run with the underscores eaten. Asterisks carry the same meaning and nothing types
 * them by accident.
 */
const INLINE = /`([^`]+)`|\*\*([\s\S]+?)\*\*|\*([^*\n]+?)\*/g;

/**
 * Inline LaTeX the models write for a symbol: "Educate $\rightarrow$ Evaluate" is an
 * arrow. Models learn it from maths text; this pane does not typeset, so it showed as
 * typed. Only these symbols, and only when every command inside the `$...$` is one of
 * them - anything else (a real formula, "$5 and $10") is left exactly as written.
 */
const LATEX_SYMBOLS: Record<string, string> = {
  rightarrow: '\u2192',
  to: '\u2192',
  longrightarrow: '\u27F6',
  leftarrow: '\u2190',
  gets: '\u2190',
  leftrightarrow: '\u2194',
  Rightarrow: '\u21D2',
  implies: '\u21D2',
  Leftarrow: '\u21D0',
  Leftrightarrow: '\u21D4',
  iff: '\u21D4',
  uparrow: '\u2191',
  downarrow: '\u2193',
  mapsto: '\u21A6',
  times: '\u00D7',
  cdot: '\u00B7',
  div: '\u00F7',
  pm: '\u00B1',
  approx: '\u2248',
  sim: '\u223C',
  ne: '\u2260',
  neq: '\u2260',
  le: '\u2264',
  leq: '\u2264',
  ge: '\u2265',
  geq: '\u2265',
  ll: '\u226A',
  gg: '\u226B',
  infty: '\u221E',
  ldots: '\u2026',
  dots: '\u2026',
  cdots: '\u22EF',
  checkmark: '\u2713',
  circ: '\u00B0',
  '%': '%',
  '$': '$',
};

const INLINE_MATH = /\$([^$\n]{1,60})\$/g;
const LATEX_COMMAND = /\\([A-Za-z]+|[%$])/g;

/** Turn `$\rightarrow$`-style symbols into the characters they stand for. */
export function plainMath(text: string): string {
  return text.replace(INLINE_MATH, (whole, inner: string) => {
    const commands = inner.match(LATEX_COMMAND);
    if (!commands || commands.some((c) => !(c.slice(1) in LATEX_SYMBOLS))) {
      return whole;
    }
    const plain = inner.replace(LATEX_COMMAND, (_c, name: string) => LATEX_SYMBOLS[name]);
    // "x ^\circ" style leftovers mean it was a formula after all.
    return /[\\^_{}]/.test(plain) ? whole : plain.trim();
  });
}

/** Split one line's text into styled runs. */
export function parseInline(text: string): Span[] {
  const spans: Span[] = [];
  let last = 0;
  INLINE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) spans.push({ text: plainMath(text.slice(last, m.index)) });
    // Code is shown as written, LaTeX and all.
    if (m[1] !== undefined) spans.push({ text: m[1], code: true });
    else if (m[2] !== undefined) spans.push({ text: plainMath(m[2]), bold: true });
    else spans.push({ text: plainMath(m[3]), italic: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) spans.push({ text: plainMath(text.slice(last)) });
  // An empty string still has to render as something, or a blank bubble collapses to
  // nothing and reads as a dropped message.
  return spans.length > 0 ? spans : [{ text }];
}

/**
 * Group lines into paragraphs, lists and headings.
 *
 * Newlines INSIDE a paragraph are kept — the renderer holds `whitespace-pre-wrap`, as
 * the raw version did. An agent that hard-wraps its own prose meant those breaks.
 */
export function parseChatMarkdown(input: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: Span[][] } | null = null;

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push({ kind: 'p', spans: parseInline(para.join('\n')) });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };
  const flush = () => {
    flushPara();
    flushList();
  };

  for (const raw of (input ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();

    if (!line.trim()) {
      flush();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'h', spans: parseInline(heading[1]) });
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = !bullet ? NUMBERED.exec(line) : null;
    if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol';
      flushPara();
      // A list that changes marker mid-run is two lists. Continuing the first would
      // renumber the second from where the first left off.
      if (list && list.kind !== kind) flushList();
      if (!list) list = { kind, items: [] };
      list.items.push(parseInline((bullet ?? numbered)![1]));
      continue;
    }

    flushList();
    para.push(line);
  }

  flush();
  return blocks;
}
